import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface ModalState {
  isOpen: boolean;
  type: string | null;
  data?: Record<string, unknown>;
}

type Theme = 'light' | 'dark' | 'system';

interface UiState {
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  mobileLeftDrawerOpen: boolean;
  mobileRightDrawerOpen: boolean;
  modal: ModalState;
  activeView: 'list' | 'board' | 'calendar';
  notifications: Notification[];
  theme: Theme;
}

interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message?: string;
  duration?: number;
  createdAt: number;
}

// Get initial theme from localStorage or system preference
const getInitialTheme = (): Theme => {
  const saved = localStorage.getItem('theme') as Theme | null;
  if (saved) return saved;
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
};

const initialState: UiState = {
  sidebarOpen: true,
  sidebarCollapsed: false,
  mobileLeftDrawerOpen: false,
  mobileRightDrawerOpen: false,
  modal: {
    isOpen: false,
    type: null,
  },
  activeView: 'list',
  notifications: [],
  theme: getInitialTheme(),
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    toggleSidebarCollapsed: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },
    openModal: (state, action: PayloadAction<{ type: string; data?: Record<string, unknown> }>) => {
      state.modal.isOpen = true;
      state.modal.type = action.payload.type;
      state.modal.data = action.payload.data;
    },
    closeModal: (state) => {
      state.modal.isOpen = false;
      state.modal.type = null;
      state.modal.data = undefined;
    },
    setActiveView: (state, action: PayloadAction<'list' | 'board' | 'calendar'>) => {
      state.activeView = action.payload;
    },
    addNotification: (state, action: PayloadAction<Omit<Notification, 'id' | 'createdAt'>>) => {
      const notification: Notification = {
        ...action.payload,
        id: crypto.randomUUID(),
        createdAt: Date.now(),
      };
      state.notifications.push(notification);
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter((n) => n.id !== action.payload);
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    toggleTheme: (state) => {
      const newTheme = state.theme === 'dark' ? 'light' : 'dark';
      state.theme = newTheme;
      localStorage.setItem('theme', newTheme);
      // Update document class
      if (typeof document !== 'undefined') {
        document.documentElement.classList.toggle('dark', newTheme === 'dark');
      }
    },
    setTheme: (state, action: PayloadAction<Theme>) => {
      state.theme = action.payload;
      localStorage.setItem('theme', action.payload);
      if (typeof document !== 'undefined') {
        document.documentElement.classList.toggle('dark', action.payload === 'dark');
      }
    },
    toggleMobileLeftDrawer: (state) => {
      state.mobileLeftDrawerOpen = !state.mobileLeftDrawerOpen;
      if (state.mobileLeftDrawerOpen) state.mobileRightDrawerOpen = false;
    },
    setMobileLeftDrawerOpen: (state, action: PayloadAction<boolean>) => {
      state.mobileLeftDrawerOpen = action.payload;
      if (action.payload) state.mobileRightDrawerOpen = false;
    },
    toggleMobileRightDrawer: (state) => {
      state.mobileRightDrawerOpen = !state.mobileRightDrawerOpen;
      if (state.mobileRightDrawerOpen) state.mobileLeftDrawerOpen = false;
    },
    setMobileRightDrawerOpen: (state, action: PayloadAction<boolean>) => {
      state.mobileRightDrawerOpen = action.payload;
      if (action.payload) state.mobileLeftDrawerOpen = false;
    },
  },
});

export const {
  toggleSidebar,
  setSidebarOpen,
  toggleSidebarCollapsed,
  openModal,
  closeModal,
  setActiveView,
  addNotification,
  removeNotification,
  clearNotifications,
  toggleTheme,
  setTheme,
  toggleMobileLeftDrawer,
  setMobileLeftDrawerOpen,
  toggleMobileRightDrawer,
  setMobileRightDrawerOpen,
} = uiSlice.actions;

export default uiSlice.reducer;
